import { inject as service } from '@ember/service';
import DS from 'ember-data';
import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import { belongsTo } from 'ember-data/relationships';
import { NAMES, TYPES, SYNC_STATUSES, AdNetworkType, AdNetworkStatus } from 'cio/utils/ad-audiences';
import SegmentModel from './segment';

export interface AudienceModelMeta {
  audienceId: string;
  audienceSize: number;
  segmentSize: number;
  syncTime: number;
  audience?: AudienceModelMetaAudience;
}

interface BaseAudienceModelMeta {
  id: string;
  accountId: string;
  name: string;
  description: string;
  [key: string]: unknown;
}

interface Status {
  code: number;
  description: string;
}

interface FacebookAudienceModelMeta extends BaseAudienceModelMeta {
  approximateCountLowerBound: number;
  approximateCountUpperBound: number;
  customerFileSource: string;
  deliveryStatus: Status;
  operationStatus: Status;
  subtype: string;
  timeContentUpdated: number;
  timeCreated: number;
  timeUpdated: number;
}

interface AdwordsAudienceModelMeta extends BaseAudienceModelMeta {
  crmBasedUserList: {
    uploadKeyType: string;
  };
  membershipLifeSpan: string;
  resourceName: string;
}

export type AudienceModelMetaAudience = FacebookAudienceModelMeta | AdwordsAudienceModelMeta;

export default class AudienceModel extends Model {
  @service store!: DS.Store;

  @attr('string') type!: AdNetworkType;
  @attr('boolean') active!: boolean;
  @attr('string') state!: AdNetworkStatus;
  @attr('string') name!: string;
  @attr('string') description!: string;
  @attr('string') adAccountId!: string;
  @attr('string') lastError!: string;
  @attr('number') createdAt!: number;
  @attr('number') updatedAt!: number;
  @attr('boolean') hasConsent!: boolean;

  @attr('camel-case') meta!: AudienceModelMeta;
  @belongsTo('segment', { async: true, inverse: 'audiences' }) segment!: DS.PromiseObject<SegmentModel>;

  get peopleSynced() {
    return this.meta?.audienceSize ?? 0;
  }

  get lastSynced() {
    return this.meta?.syncTime ?? 0;
  }

  get status() {
    return SYNC_STATUSES[this.state];
  }

  get adNetworkName() {
    return NAMES[this.type];
  }

  get isFacebook() {
    return this.type === TYPES.facebook;
  }

  get isAdwords() {
    return this.type === TYPES.adwords;
  }

  get paused() {
    return this.state === SYNC_STATUSES.paused.id;
  }

  get syncing() {
    return this.state === SYNC_STATUSES.syncing.id;
  }

  get synced() {
    return this.state === SYNC_STATUSES.synced.id;
  }

  get failed() {
    return this.state === SYNC_STATUSES.failed.id;
  }

  get error() {
    return this.state === SYNC_STATUSES.error.id;
  }

  get credential() {
    let credentials = this.store.peekAll('audience-credential');
    return credentials.findBy('type', this.type);
  }

  get adAccount() {
    let credentials = this.store.peekAll('audience-credential');
    let credential = credentials.findBy('type', this.type);

    if (credential == null) {
      return null;
    }

    let accountId = this.meta?.audience?.accountId;

    if (accountId == null) {
      return null;
    }

    return credential[this.type]?.adAccounts?.find((a) => String(a.accountId) === String(accountId)) ?? null;
  }

  get adAccountName() {
    return this.adAccount != null ? this.adAccount.name : this.adNetworkName;
  }

  get confirmConsent() {
    return this.state === SYNC_STATUSES.confirm_consent.id;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    audience: AudienceModel;
  }
}
